import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Lock } from "lucide-react";
import { gbp } from "../lib/shop";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

export default function ProductCard({ product, index = 0 }) {
  const [hover, setHover] = useState(false);
  const { add } = useCart();
  const navigate = useNavigate();

  const secondImage = product.images[1] || product.images[0];

  const quickAdd = (size) => {
    const variant = product.variants.find((v) => v.size === size && v.available);
    if (!variant) {
      navigate(`/product/${product.handle}`);
      return;
    }
    add({
      variantId: variant.id,
      handle: product.handle,
      title: product.title,
      colour: variant.colour,
      size: variant.size,
      price: product.price,
      image: product.images[0],
      preorder: product.preorder,
      sellingPlanId: product.sellingPlanId || null,
    });
    toast.success(`Added — ${product.title} (${variant.colour} / ${variant.size})`);
  };

  return (
    <div
      className="group relative"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`product-card-${product.handle}`}
    >
      <Link to={`/product/${product.handle}`} className="block relative overflow-hidden bg-card aspect-[4/5]">
        <img
          src={product.images[0]}
          alt={product.title}
          loading={index < 2 ? "eager" : "lazy"}
          className={`absolute inset-0 w-full h-full object-contain transition-all duration-700 ease-out ${
            hover ? "opacity-0" : "opacity-100"
          }`}
        />
        <img
          src={secondImage}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-contain transition-all duration-700 ease-out ${
            hover ? "opacity-100" : "opacity-0"
          }`}
        />
        <div className="absolute top-3 left-3 flex flex-col gap-2 items-start">
          {product.tag && (
            <span className="bg-bone text-ink font-mono text-[9px] tracking-[0.18em] px-2.5 py-1.5">{product.tag}</span>
          )}
          {product.badge && (
            <span className="bg-ember text-white font-mono text-[9px] tracking-[0.18em] px-2.5 py-1.5" data-testid={`product-scarcity-${product.handle}`}>
              {product.badge}
            </span>
          )}
        </div>
      </Link>

      {/* quick add strip */}
      <div
        className={`absolute left-0 right-0 bottom-[76px] md:bottom-[76px] px-3 transition-all duration-400 ${
          hover ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"
        }`}
      >
        {product.sizes.length > 1 ? (
          <div className="flex flex-wrap gap-1.5 justify-center" data-testid={`product-quick-add-${product.handle}`}>
            {product.sizes.map((s) => {
              const available = product.variants.some((v) => v.size === s && v.available);
              return (
                <button
                  key={s}
                  onClick={() => quickAdd(s)}
                  disabled={!available}
                  className={`font-mono text-[10px] tracking-wider px-2.5 py-2 min-w-[38px] transition-colors duration-200 ${
                    available
                      ? "bg-bone text-ink hover:bg-ember hover:text-white"
                      : "bg-ink/80 text-ash line-through cursor-not-allowed"
                  }`}
                  data-testid={`product-size-select-${product.handle}-${s}`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        ) : (
          <button
            onClick={() => quickAdd(product.sizes[0])}
            className="w-full flex items-center justify-center gap-2 bg-bone text-ink font-mono text-[10px] tracking-[0.2em] py-3 hover:bg-ember hover:text-white transition-colors duration-300"
            data-testid={`product-quick-add-${product.handle}`}
          >
            <Plus className="w-3.5 h-3.5" /> QUICK ADD
          </button>
        )}
      </div>

      <div className="pt-4 flex items-start justify-between gap-4">
        <div>
          <Link to={`/product/${product.handle}`} className="font-display font-bold uppercase tracking-tight text-sm md:text-base leading-tight hover:text-ash transition-colors">
            {product.title}
          </Link>
          <p className="font-mono text-[10px] tracking-[0.15em] text-ash uppercase mt-1">
            {product.colors.map((c) => c.name).join(" / ")}
          </p>
        </div>
        <span className="font-mono text-sm md:text-base shrink-0">{gbp(product.price)}</span>
      </div>
    </div>
  );
}
